import {
  Box,
  Button,
  Flex,
  Text,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tabs,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { SingleVacancyBlock } from "@/components/VacancyBlocks/SingleVacancyBlock/SingleVacancyBlock";
import { HistoryVacancyBlock } from "@/components/VacancyBlocks/SingleVacancyBlock/HistoryVacancyBlock";
import { Layout } from "@/components/Layout/Layout";
import { useAppDispatch, useAppSelector } from "@/store";
import { useEffect, useState, useMemo } from "react";
import { fetchSingleVacancy } from "@/store/slices/vacancy";
import { ExportVacancyModal } from "@/components/ExportVacancyModal/ExportVacancyModal";
import PageLoaderWrapper from "@/components/PageLoaderWrapper";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { FiClock, FiEdit, FiExternalLink, FiUserPlus } from "react-icons/fi";
import { ChakraTabs } from "@/components/tabs/ChakraTabs";
import { CandidateTable } from "@/components/VacancyBlocks/CandidateTable/CandidateTable";
import { createColumnHelper } from "@tanstack/react-table";
import { VacancyApplication } from "@/model/vacancy";
import { useSearchParams } from "react-router-dom";
import { formatDate } from "@/helpers/formatDate";
import { AddCandidateToVacancyModal } from "@/components/AddCandidateToVacancyModal/AddCandidateToVacancyModal";

export const SingleVacancyPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { currentVacancy, isLoading } = useAppSelector((state) => state.vacancy);
  const [isShowHistory, setShowHistory] = useState(false);
  const [searchParams] = useSearchParams();
  const [isAddCandidateOpen, setIsAddCandidateOpen] = useState(false);

  const hrCount = Number(((currentVacancy as any)?.candidates?.hr?.items?.length as number) || 0);
  const schoolCount = Number(((currentVacancy as any)?.candidates?.school?.items?.length as number) || 0);

  const columnHelper = createColumnHelper<VacancyApplication>();
  const candidateColumns = [
    columnHelper.accessor(
      ({ candidate }) => `${candidate?.last_name ?? ""} ${candidate?.first_name ?? ""} ${candidate?.patronymic_name ?? ""}`.trim(),
      {
        id: "candidate.fio",
        header: "ФИО",
        cell: (info) => (
          <Text textDecor="underline" sx={{ "&:hover": { cursor: "pointer", opacity: 0.7 } }}>
            <a style={{ width: "100%", height: "100%", display: "block" }} href={`/vacancy/${info.row.original.id}/annex`}>
              {info.getValue()}
            </a>
          </Text>
        ),
      },
    ),
    columnHelper.accessor((row) => row?.status?.name, { id: "status.name", header: "Этап", cell: (info) => info.getValue() || "" }),
    columnHelper.accessor((row) => row?.created_by?.username, {
      id: "created_by.username",
      header: "Ответственный рекрутер",
      cell: (info) => info.getValue() || "",
    }),
    columnHelper.accessor((row) => (row as any)?.candidate?.internal_rate as any, {
      id: "candidate.internal_rate",
      header: "Внутренняя ставка кандидата",
      cell: (info) => info.getValue() || "",
    }),
    columnHelper.accessor((row) => (row as any)?.candidate?.source?.name, {
      id: "candidate.source.name",
      header: "Источник",
      cell: (info) => info.getValue() || "",
    }),
    columnHelper.accessor((row) => row?.interview_at, {
      id: "interview_at",
      header: "Дата собеседования/ТЗ",
      cell: (info) => (info.getValue() ? formatDate(info.getValue() as number, true) : ""),
    }),
    columnHelper.accessor((row) => row?.created_at, {
      id: "created_at",
      header: "Дата прикрепления кандидата",
      cell: (info) => <Text>{formatDate(info.getValue() as number)}</Text>,
    }),
  ];

  const visibleTabs = useMemo(() => {
    const tabs = [{ id: "details", label: "Детали" }];

    if (currentVacancy?.candidates?.hr?.visible) {
      tabs.push({ id: "hr", label: `Кандидаты (HR) ${hrCount}` });
    }

    if (currentVacancy?.candidates?.school?.visible) {
      tabs.push({ id: "school", label: `Кандидаты (Школа) ${schoolCount}` });
    }

    return tabs;
  }, [currentVacancy?.candidates?.hr?.visible, currentVacancy?.candidates?.school?.visible, hrCount, schoolCount]);

  // Определяем видимые панели табов
  const visibleTabPanels = useMemo(() => {
    const panels = [
      <TabPanel key="details" paddingX={0} paddingTop={0}>
        <SingleVacancyBlock />
      </TabPanel>,
    ];

    if (currentVacancy?.candidates?.hr?.visible) {
      panels.push(
        <TabPanel key="hr" paddingX={0}>
          <CandidateTable list={((currentVacancy as any)?.candidates?.hr?.items || []) as any} columns={candidateColumns} />
        </TabPanel>,
      );
    }

    if (currentVacancy?.candidates?.school?.visible) {
      panels.push(
        <TabPanel key="school" paddingX={0}>
          <CandidateTable list={((currentVacancy as any)?.candidates?.school?.items || []) as any} columns={candidateColumns} />
        </TabPanel>,
      );
    }

    return panels;
  }, [
    currentVacancy?.candidates?.hr?.visible,
    currentVacancy?.candidates?.school?.visible,
    currentVacancy?.candidates?.hr?.items,
    currentVacancy?.candidates?.school?.items,
    candidateColumns,
  ]);

  const getActiveTabIndex = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabIndex = visibleTabs.findIndex((tab) => tab.id === tabParam);
      if (tabIndex !== -1) {
        return tabIndex;
      }
    }
    return 0;
  };

  const activeTabIndex = getActiveTabIndex();

  useEffect(() => {
    if (id !== undefined) {
      dispatch(fetchSingleVacancy({ params: { id: Number(id) } }));
    }
  }, [id, dispatch]);

  return (
    <Layout>
      <PageLoaderWrapper isLoading={isLoading} loadingText="Загрузка данных вакансии...">
        <Flex flexDirection="column" gap="24px" paddingRight="32px" maxH="90vh" overflowY="auto">
          <Text color="gray.500" cursor="pointer" onClick={() => navigate("/vacancy")} display="flex" alignItems="center" gap="8px">
            <ArrowBackIcon />
            Назад к вакансиям
          </Text>
          <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
            <Flex alignItems="center" justifyContent="space-between">
              <Flex direction="column" gap="8px">
                <Text fontSize={24} fontWeight={500}>
                  {currentVacancy?.name || currentVacancy?.application?.name || "Вакансия"}
                </Text>
              </Flex>
              <Flex gap="12px" alignItems="center">
                {currentVacancy?.application?.id && (
                  <Button
                    leftIcon={<Icon as={FiExternalLink as any} boxSize={4} />}
                    onClick={() => navigate(`/applications/${currentVacancy.application.id}`)}
                  >
                    Перейти к заявке
                  </Button>
                )}
                <Button leftIcon={<Icon as={FiEdit as any} boxSize={4} />} onClick={() => navigate("edit")}>
                  Редактировать
                </Button>
                <Button leftIcon={<Icon as={FiClock as any} boxSize={4} />} variant="outline" onClick={() => setShowHistory(true)}>
                  История
                </Button>
                {id && <ExportVacancyModal vacancyId={Number(id)} />}
              </Flex>
            </Flex>
          </Box>
          <Modal isCentered isOpen={isShowHistory} onClose={() => setShowHistory(false)} size="6xl" scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(3px)" />
            <ModalContent maxW="60vw" px="24px" paddingTop="32px" paddingBottom="24px">
              <ModalHeader p={0}>История изменений</ModalHeader>
              <ModalCloseButton />
              <ModalBody p={0} pt="8px" maxH="70vh" overflowY="auto">
                <HistoryVacancyBlock />
              </ModalBody>
            </ModalContent>
          </Modal>
          <Flex flexDirection="column" gap="32px">
            <Tabs index={activeTabIndex} isLazy>
              <Flex alignItems="center" justifyContent="space-between" gap="12px" mb="8px">
                <Box flex="1 1 auto" w="max-content">
                  <ChakraTabs tabs={visibleTabs} baseUrl={`/vacancy/${id}`} />
                </Box>
                {(visibleTabs[activeTabIndex]?.id === "hr" || visibleTabs[activeTabIndex]?.id === "school") && (
                  <Button leftIcon={<Icon as={FiUserPlus as any} boxSize={4} />} colorScheme="blue" onClick={() => setIsAddCandidateOpen(true)}>
                    Добавить кандидата
                  </Button>
                )}
              </Flex>
              <TabPanels paddingTop="16px">{visibleTabPanels}</TabPanels>
            </Tabs>
          </Flex>
          {id && <AddCandidateToVacancyModal isOpen={isAddCandidateOpen} vacancyId={Number(id)} onClose={() => setIsAddCandidateOpen(false)} />}
        </Flex>
      </PageLoaderWrapper>
    </Layout>
  );
};
